import * as React from '@emotion/react';
import styled from '@emotion/styled';
import {
  createRef,
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import { messages } from '../messages';
import {
  MediaContainerProps,
  MediaContainerRootProps,
  MediaWrapperProps
} from '../models/media-props';
import {
  INTERSECTION_OPTIONS,
  MEDIA_TYPE,
  NUM_OF_CARDS
} from '../utils/constants';

export function MediaContainer({
  selectedMedia,
  componentOrder,
  mediaType,
  mediaSourceLinks,
  intl,
  inViewChange,
  videoEnded
}: MediaContainerProps) {
  const [isInView, setIsInView] = useState<boolean>(false);
  const containerRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const handleIntersect = (entries: any[]) => {
    const entry = entries[0] || {};
    const { isIntersecting } = entry;
    setIsInView(!!isIntersecting);
    inViewChange(!!isIntersecting);
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      handleIntersect,
      INTERSECTION_OPTIONS
    );
    observer.observe(containerRef.current);
  }, [containerRef]);

  return (
    <MediaContainerRoot ref={containerRef} componentOrder={componentOrder}>
      <MediaWrapper selectedMedia={selectedMedia}>
        {renderMedia(
          mediaType,
          mediaSourceLinks,
          intl,
          selectedMedia,
          videoEnded,
          isInView
        )}
      </MediaWrapper>
    </MediaContainerRoot>
  );
}

const renderMedia = (
  mediaType: MEDIA_TYPE,
  mediaSourceLinks: string[],
  intl: ReactIntl.InjectedIntl,
  selectedMedia: number,
  videoEnded: () => void,
  isInView: boolean
) => {
  const videoRefMap = new Map<number, RefObject<HTMLVideoElement>>();
  if (mediaType === MEDIA_TYPE.VIDEO) {
    // Auto start video when it is selected card
    useEffect(() => {
      for (const [videoId, video] of videoRefMap.entries()) {
        if (!video?.current || !video.current.play) {
          continue;
        }
        // play from start when video is selected and in view
        if (videoId === selectedMedia && isInView) {
          video.current.currentTime = 0;
          video.current.play();
        } else {
          video.current.pause();
        }
      }
    }, [selectedMedia, isInView]);
  }

  const mediaList = [];
  for (let cardId = 1; cardId <= NUM_OF_CARDS; cardId++) {
    const props = selectedMedia === cardId ? { tabIndex: 1 } : { tabIndex: -1 };

    let mediaContent;
    if (mediaType === MEDIA_TYPE.VIDEO) {
      const ref = createRef<HTMLVideoElement>();

      mediaContent = (
        <video
          ref={ref}
          width="100%"
          height="100%"
          object-fit="fill"
          preload="auto"
          muted={true}
          tabIndex={-1}
          onEnded={() => videoEnded()}
          playsInline={true}
        >
          <source src={mediaSourceLinks[cardId - 1]} />
          {intl.formatMessage(messages.embeddedVideosError)}
        </video>
      );

      videoRefMap.set(cardId, ref);
    } else if (mediaType === MEDIA_TYPE.IMAGE) {
      mediaContent = (
        <img
          width="100%"
          height="auto"
          src={mediaSourceLinks[cardId - 1]}
          // @ts-ignore
          alt={intl.formatMessage(messages[`altTextImage${cardId}`])}
        />
      );
    }

    mediaList.push(
      <Media key={cardId} className={`media media${cardId}`} {...props}>
        {mediaContent}
      </Media>
    );
  }
  return mediaList;
};

export const MediaWrapper = styled.div<MediaWrapperProps>`
  display: flex;
  justify-content: flex-start;
  flex-wrap: nowrap;
  overflow: hidden;
  flex-direction: row;

  .media img {
    object-fit: contain;
    object-position: top;
    margin-bottom: 0;
  }

  @media only screen and (min-width: 768px) {
    .media {
      order: 2;
    }

    .media.media${(props) => props.selectedMedia} {
      order: 1;
    }
  }

  @media only screen and (max-width: 767px) {
    min-height: 216px;
    margin-right: 0px;
    margin-left: 0px;
    transition: transform 0.3s;
    transform: ${(props) => `translateX(-${(props.selectedMedia - 1) * 100}%)`};
    overflow: visible;
  }
`;

export const MediaContainerRoot = styled.div<MediaContainerRootProps>`
  display: flex;
  justify-content: flex-start;
  align-content: start;
  flex-wrap: nowrap;
  flex-direction: row;
  overflow: visible;
  flex-basis: calc(99.9999% / 12 * 8);
  max-width: calc(99.9999% / 12 * 8);
  order: ${(props) => props.componentOrder};
  margin-left: 16px;
  margin-bottom: auto;

  @media only screen and (max-width: 1023px) {
    flex-basis: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
    order: 2;
  }
`;

export const Media = styled.div`
  flex-basis: inherit;
  min-width: 100%;
  display: inline-flex;
`;
